/* ============================================================
   NORTHSHEEP — Floating frosted mobile header (collection pages)
   Lightweight: passive scroll listener throttled with rAF, no libraries.
   Hidden by default; shows on a slight upward scroll after an activation
   offset; hides on downward scroll and near the top. Reuses the existing
   menu drawer, Favourites system, and cart drawer.
   ============================================================ */
(function () {
  'use strict';
  var el = document.querySelector('[data-ns-float]');
  if (!el) return;

  var offset = parseInt(el.getAttribute('data-offset'), 10);
  if (isNaN(offset)) offset = 120;
  /* Named triggerPx, not `trigger`: a `function trigger(target)` is declared
     further down for opening the cart. Function declarations hoist, so this
     var assignment overwrote it at load time and every call to open the cart
     from this header threw "trigger is not a function" — the function below was
     unreachable dead code. */
  var triggerPx = parseInt(el.getAttribute('data-trigger'), 10);
  if (isNaN(triggerPx)) triggerPx = 24;
  var NEAR_TOP = 80;

  var mq = window.matchMedia('(max-width: 749px)');
  var lastY = window.pageYOffset || 0;
  var accumUp = 0;
  var visible = false;
  var ticking = false;

  function setVisible(v) {
    if (v === visible) return;
    visible = v;
    el.classList.toggle('is-visible', v);
  }

  /* Only a collection page reveals this on scroll. Everywhere else the header
     is rendered but silent until something is added to the basket or to
     favourites — see NsFloatHeader.flash below. */
  var scrollReveal = el.hasAttribute('data-ns-float-scroll');

  /* A RESTORED POSITION IS NOT A GESTURE (owner, 21 Sep 2026: "when i back
     and forth the floating header appears ... pushing me down a bit").

     Coming back to a collection, the browser sets the scroll in one step. The
     rule below reads any upward movement as "the shopper is scrolling back
     up" and reveals the bar — which then covers the top of the viewport,
     hiding the row they were looking at. Nothing moves (the bar is fixed),
     but being covered reads exactly like being pushed down.

     Nobody scrolled. So for a moment after a back or a reload the bar stays
     out of it, and the first real gesture is what wakes it. */
  var restoring = (function () {
    try {
      var nav = (performance.getEntriesByType('navigation')[0] || {}).type;
      return nav === 'back_forward' || nav === 'reload';
    } catch (e) {
      return false;
    }
  })();
  if (restoring) {
    window.setTimeout(function () {
      restoring = false;
    }, 1500);
    ['touchmove', 'wheel', 'keydown'].forEach(function (evt) {
      window.addEventListener(
        evt,
        function once() {
          restoring = false;
          window.removeEventListener(evt, once);
        },
        { passive: true }
      );
    });
  }

  function onScroll() {
    if (!scrollReveal) return;
    if (restoring) {
      /* Track the position so the first real gesture measures from here, but
         do not reveal anything on the strength of the restore itself. */
      lastY = window.pageYOffset < 0 ? 0 : window.pageYOffset;
      return;
    }
    if (!mq.matches) { setVisible(false); return; }
    var y = window.pageYOffset;
    if (y < 0) y = 0;
    var dy = y - lastY;
    if (y <= NEAR_TOP) {
      // Near the top the normal header takes over.
      setVisible(false);
      accumUp = 0;
    } else if (y > offset) {
      if (dy < -1) {
        // Scrolling up — reveal once the upward movement passes the threshold.
        accumUp += -dy;
        if (accumUp >= triggerPx) setVisible(true);
      } else if (dy > 1) {
        // Scrolling down — hide and reset.
        accumUp = 0;
        setVisible(false);
      }
    }
    lastY = y;
  }

  window.addEventListener('scroll', function () {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(function () { onScroll(); ticking = false; });
    }
  }, { passive: true });

  // Keep state sane when crossing the desktop breakpoint.
  /* Crossing the breakpoint mid-visit puts the header away — but only when it
     is there because of scrolling. A flash in progress is left to its own
     timer, or resizing a window would snatch the answer away mid-animation. */
  if (mq.addEventListener) {
    mq.addEventListener('change', function () {
      if (!mq.matches && !flashTimer) setVisible(false);
    });
  }

  // Trigger an existing (off-screen) control WITHOUT the browser scrolling to
  // it: focus it with preventScroll first, so the click that follows doesn't
  // move focus again (which is what jumped the page to the top).
  function trigger(target) {
    if (!target) return;
    try { if (target.focus) target.focus({ preventScroll: true }); } catch (e) {}
    target.click();
  }

  // ---- brand: mirror exactly what the real header shows (logo OR wordmark) ----
  var realBrand = document.querySelector('.header-wrapper .header__heading-link');
  var floatBrand = el.querySelector('.header__heading-link');
  if (realBrand && floatBrand) floatBrand.innerHTML = realBrand.innerHTML;

  // ---- menu: open our OWN overlay in place (the header's menu drawer is
  // anchored to the document top, so triggering it always scrolls there). No
  // scroll, no body lock — just like the cart/favourites drawers. ----
  var menuBtn = el.querySelector('[data-ns-float-menu]');
  var menuPanel = document.querySelector('[data-ns-float-menu-panel]');
  var menuMount = menuPanel ? menuPanel.querySelector('[data-ns-float-menu-mount]') : null;
  var menuBuilt = false;
  // Clone the REAL header menu drawer into the overlay so it's a 1:1 copy
  // (same Dawn markup/classes/CSS, search, submenus, social, localization).
  function buildMenu() {
    if (menuBuilt || !menuMount) return;
    var real = document.getElementById('menu-drawer');
    if (!real) return;
    var clone = real.cloneNode(true);
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach(function (n) { n.removeAttribute('id'); });
    // The cloned close button's inline onclick targets header-drawer (which the
    // clone isn't inside) — repoint it to our overlay close.
    clone.querySelectorAll('.menu-drawer__close-top').forEach(function (b) {
      b.removeAttribute('onclick');
      b.setAttribute('data-ns-float-menu-close', '');
    });
    menuMount.appendChild(clone);
    menuBuilt = true;
  }
  /* FOCUS IN, AND BACK OUT (audit, 23 Sep 2026). The menu opened over the
     page with focus left behind on the page (measured: on <body>), so a
     screen reader or keyboard carried on through what the overlay covers,
     and the button never said it was expanded. Focus now moves to the menu's
     close button, and returns to the menu button on close — only if it was
     still inside the menu. After a tap this draws no focus ring: a focus
     moved by script only shows one when the previous focus did. */
  /* Not before the menu's stylesheet has arrived (owner, 4 Oct 2026: category text and pictures at the foot of a
     menu opened while the page was loading). The clone uses the same sub-menu styles as the header's drawer,
     which come from component-menu-drawer.css, loaded without holding the page; see nsMenuStyled in global.js. */
  var styleWait = null;
  function menuStyled(retry) {
    var link = document.getElementById('ns-menu-drawer-css');
    if (!link || link.media === 'all') return true;
    if (link.sheet) { link.media = 'all'; return true; }
    if (!styleWait) {
      var done = function () {
        link.media = 'all';
        var go = styleWait;
        styleWait = null;
        if (go) go();
      };
      link.addEventListener('load', done, { once: true });
      link.addEventListener('error', done, { once: true });
      setTimeout(done, 6000);
    }
    styleWait = retry;
    return false;
  }
  function openMenu() {
    if (!menuPanel) return;
    if (!menuStyled(openMenu)) return;
    menuPanel.style.bottom = ''; // a size pinned by a tapped link that went nowhere (see the click handler)
    menuPanel.style.height = '';
    buildMenu();
    menuPanel.hidden = false;
    void menuPanel.offsetWidth; // reflow so the open transition runs
    menuPanel.classList.add('is-open');
    menuPanel.setAttribute('aria-hidden', 'false');
    if (menuBtn) menuBtn.setAttribute('aria-expanded', 'true');
    // The BUTTON: the scrim carries the same attribute and cannot take focus.
    var first = menuPanel.querySelector('button[data-ns-float-menu-close]');
    if (first) {
      try { first.focus({ preventScroll: true }); } catch (e) {}
    }
  }
  // Replicate Dawn's submenu slide for the clone (it has no MenuDrawer JS):
  // open = slide the sub-panel over + hide the parent list; close = slide back.
  function openSubmenu(det) {
    var parentMenu = det.closest('.has-submenu');
    det.setAttribute('open', '');
    requestAnimationFrame(function () {
      det.classList.add('menu-opening');
      if (parentMenu) parentMenu.classList.add('submenu-open');
    });
  }
  function closeSubmenu(det) {
    var parentMenu = det.closest('.has-submenu');
    det.classList.remove('menu-opening');
    if (parentMenu) parentMenu.classList.remove('submenu-open');
    // Must outlast the submenu's exit transition (--ns-dur-exit, 300ms, in
    // ns-motion.css), or removing [open] cuts the slide off before it ends.
    setTimeout(function () { det.removeAttribute('open'); }, 340);
  }
  function resetSubmenus() {
    if (!menuMount) return;
    menuMount.querySelectorAll('details[open]').forEach(function (d) { d.removeAttribute('open'); d.classList.remove('menu-opening'); });
    menuMount.querySelectorAll('.submenu-open').forEach(function (n) { n.classList.remove('submenu-open'); });
  }
  function closeMenu() {
    if (!menuPanel) return;
    var focusWasInside = menuPanel.contains(document.activeElement);
    menuPanel.classList.remove('is-open');
    menuPanel.setAttribute('aria-hidden', 'true');
    if (menuBtn) {
      menuBtn.setAttribute('aria-expanded', 'false');
      if (focusWasInside) {
        try { menuBtn.focus({ preventScroll: true }); } catch (e) {}
      }
    }
    // `hidden` is display:none, so it must wait for the panel's exit
    // transition (--ns-dur-exit, 300ms, in ns-motion.css) to finish.
    setTimeout(function () {
      if (!menuPanel.classList.contains('is-open')) { menuPanel.hidden = true; resetSubmenus(); }
    }, 340);
  }
  if (menuBtn) menuBtn.addEventListener('click', openMenu);
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    if (e.target.closest('[data-ns-float-menu-close]')) { closeMenu(); return; }
    if (!e.target.closest('.ns-float-menu')) return;
    // Submenu parent (a <summary>) — slide its sub-panel in/out.
    var sum = e.target.closest('summary.menu-drawer__menu-item');
    if (sum) {
      e.preventDefault();
      var det = sum.parentNode;
      if (det && det.tagName === 'DETAILS') {
        if (det.classList.contains('menu-opening')) closeSubmenu(det);
        else openSubmenu(det);
      }
      return;
    }
    // Submenu back button — slide back to the main menu.
    var back = e.target.closest('.menu-drawer__close-button');
    if (back) { e.preventDefault(); var d = back.closest('details'); if (d) closeSubmenu(d); return; }
    /* A link: the next page starts loading, and an iPhone brings its toolbar back meanwhile. The overlay is
       inset: 0, so it shrank with the screen and its foot jumped up (owner, 4 Oct 2026: "i still see the menu
       jumping up before loading a page"). Its size is pinned at the tap; the toolbar covers its foot instead.
       A return from the back-forward cache lets it go again (below). */
    var link = e.target.closest('a[href]');
    if (link && !e.defaultPrevented && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && link.target !== '_blank' && (link.getAttribute('href') || '').charAt(0) !== '#' && menuPanel) {
      // Height first: with the bottom let go, the overlay measures 0 (all its children are absolutely placed).
      var pinned = menuPanel.offsetHeight;
      menuPanel.style.height = pinned + 'px';
      menuPanel.style.bottom = 'auto';
    }
  });
  window.addEventListener('pageshow', function () {
    if (menuPanel) { menuPanel.style.bottom = ''; menuPanel.style.height = ''; }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && menuPanel && menuPanel.classList.contains('is-open')) closeMenu();
  });

  // ---- cart: open the existing cart drawer in place (no off-screen click) ----
  var cartBtn = el.querySelector('[data-ns-float-cart]');
  if (cartBtn) cartBtn.addEventListener('click', function () {
    var cd = document.querySelector('cart-drawer');
    if (cd && typeof cd.open === 'function') { cd.open(cartBtn); return; }
    var c = document.getElementById('cart-icon-bubble');
    if (c) trigger(c);
    else window.location.href = (window.routes && window.routes.cart_url) || '/cart';
  });

  /* ============================================================
     "IT WENT UP THERE" — the header answers, instead of a message.

     Owner, 20 Sep 2026: "on mobile when you add something to favourites or
     cart it shows then this message 'added to basket' instead can you let the
     floating header come down and then put small animation or something that
     the cart count or favourite count changed?"

     It is a better answer than a toast, and for a reason worth writing down:
     a message SAYS where the piece went, this SHOWS it. The header comes down,
     the number it landed in counts up, and the shopper now knows both that it
     worked and where to find it — with nothing to read and nothing to dismiss.
     It also points at a control that is still there after the animation, which
     a toast never is.

     NEAR THE TOP IT STAYS PUT. The real header is already on screen there, and
     sliding a second one over it would be a glitch, so the real bubble takes
     the pop instead.

     Public, because the two things that add are elsewhere: ns-favorites.js and
     ns-basket-open.js. Returns false when it could not do it — on a desktop,
     or where there is no floating header — so the caller can fall back to its
     toast rather than leaving the shopper with no answer at all.
     ============================================================ */
  var flashTimer = 0;
  function popBadge(badge) {
    if (!badge) return;
    badge.classList.remove('ns-float-pop');
    /* Restart the animation on a second add: removing the class and reading
       layout forces the browser to treat the next add as a new animation
       rather than ignoring a class it already has. */
    void badge.offsetWidth;
    badge.classList.add('ns-float-pop');
    setTimeout(function () {
      badge.classList.remove('ns-float-pop');
    }, 700);
  }

  /* Is the shop's real header still on the screen? Asked by measurement
     rather than by scroll position, because the answer depends on things this
     file does not own: whether the merchant has set the header to stick, how
     tall it is, and whether the page has been scrolled past it at all.

     If it is there, the count it already shows is the right place to answer —
     and dropping a second header over the first one would be a glitch. */
  function realHeaderOnScreen() {
    var h = document.querySelector('.header-wrapper') || document.getElementById('cart-icon-bubble');
    if (!h) return false;
    var r = h.getBoundingClientRect();
    return r.bottom > 8 && r.top < window.innerHeight;
  }

  window.NsFloatHeader = {
    flash: function (which) {
      /* Desktops get this too (owner, 20 Sep 2026: "for desktop can you also
         design a floating header that only appears shortly when adding
         something to cart or favourites when the header is out of sight?").
         The header on this shop does not stick, so past the first screen there
         is nothing on a desktop to show that anything happened either — the
         note in the corner was doing that job, and a control the shopper can
         actually open does it better. Only the scroll reveal stays a phone
         behaviour; see scrollReveal above. */
      var nearTop = realHeaderOnScreen();
      var host = nearTop ? document : el;
      var badge =
        which === 'fav'
          ? host.querySelector('[data-ns-fav-count]')
          : host.querySelector('.cart-count-bubble:not([data-ns-fav-count])');

      if (nearTop) {
        /* The real header is on screen; just count up in it. */
        popBadge(badge);
        return true;
      }

      setVisible(true);
      /* The count is written by whoever added the thing, and on the floating
         header it arrives through the mirror below — a frame or two later.
         The pop waits for it so the number is already new when it bounces. */
      setTimeout(function () {
        popBadge(
          which === 'fav'
            ? el.querySelector('[data-ns-fav-count]')
            : el.querySelector('.cart-count-bubble:not([data-ns-fav-count])')
        );
      }, 60);

      window.clearTimeout(flashTimer);
      flashTimer = window.setTimeout(function () {
        setVisible(false);
        /* Hand the state back to the scroll logic cleanly: without this the
           upward travel banked before the flash would re-open the header the
           moment the shopper moved a finger. */
        accumUp = 0;
        lastY = window.pageYOffset || 0;
      }, 2600);
      return true;
    }
  };

  // ---- mirror the whole cart icon (svg + count) from the real header ----
  function mirrorCart() {
    if (!cartBtn) return;
    var src = document.getElementById('cart-icon-bubble');
    if (src) cartBtn.innerHTML = src.innerHTML;
  }
  mirrorCart();
  var cartIcon = document.getElementById('cart-icon-bubble');
  if (cartIcon && window.MutationObserver) {
    new MutationObserver(mirrorCart).observe(cartIcon, { childList: true, subtree: true });
  }
})();


/* ------------------------------------------------------------------
   THE PILL LINES UP WITH THE HEADER IT REPLACES (owner, 22 Sep 2026).

   Desktop only, where the pill is a corner rather than a bar. Its right offset
   used to be a fixed 20px, which can only be right at one window width: the
   header's icons live inside a 1400px page with a 50px gutter, so their
   distance from the window edge changes as the window does.

   So it is measured instead. The two icons in the real header give three
   numbers — their widths, the gap between them, and how far the last one ends
   from the window's right edge — and the pill is built from those. Both icons
   then sit exactly above their counterparts at every width, and stay there if
   the header is ever restyled.

   Nothing here runs on scroll: horizontal geometry does not change when the
   page moves, so load and resize are enough.
   ------------------------------------------------------------------ */
(function () {
  var bar = document.querySelector('.ns-float-header');
  if (!bar) return;

  function realIcons() {
    var header = document.querySelector('.header-wrapper .header, header.header');
    if (!header) return null;
    var icons = [].slice
      .call(header.querySelectorAll('.header__icons > .header__icon, .header__icons > a, .header__icons > form'))
      .map(function (el) {
        return el.getBoundingClientRect();
      })
      .filter(function (r) {
        return r.width > 0;
      });
    /* The pill carries the last two — favourites and the basket. */
    if (icons.length < 2) return null;
    return { fav: icons[icons.length - 2], cart: icons[icons.length - 1] };
  }

  function align() {
    if (!window.matchMedia('(min-width: 750px)').matches) {
      bar.style.removeProperty('--ns-float-right');
      bar.style.removeProperty('--ns-float-gap');
      bar.style.removeProperty('--ns-float-fav-w');
      bar.style.removeProperty('--ns-float-cart-w');
      return;
    }
    var r = realIcons();
    if (!r) return;
    var gap = r.cart.left - r.fav.right;
    if (gap < 0) return; /* wrapped or overlapping: leave the fallback alone */
    bar.style.setProperty('--ns-float-gap', Math.round(gap) + 'px');
    bar.style.setProperty('--ns-float-fav-w', Math.round(r.fav.width) + 'px');
    bar.style.setProperty('--ns-float-cart-w', Math.round(r.cart.width) + 'px');
    roomForCount();

    /* TWO PASSES, BECAUSE THE PILL HAS A FRAME OF ITS OWN.

       Placing the bar so its EDGE matches the header's last icon leaves the
       icon 16px short, because the pill carries a border and padding between
       its edge and its contents. Rather than hard-code that 16 — which would
       be another number to get wrong the next time the pill is restyled — the
       first pass places the bar, the second measures how far its own basket
       actually landed from the header's, and moves it by exactly that. One
       correction is exact: shifting the bar shifts the icon with it. */
    var base = Math.round(window.innerWidth - r.cart.right);
    bar.style.setProperty('--ns-float-right', base + 'px');
    var own = bar.querySelector('.header__icons .header__icon--cart') ||
      bar.querySelector('.header__icons .header__icon:last-child');
    if (!own) return;
    var drift = own.getBoundingClientRect().right - r.cart.right;
    if (Math.abs(drift) > 0.5) {
      bar.style.setProperty('--ns-float-right', Math.round(base + drift) + 'px');
    }
  }

  /* ROOM FOR THE BASKET'S COUNT (owner, 23 Sep 2026: "the pills has to be
     original size when there is nothing added to cart but the pill has to
     grow a bit when adding something to cart so that the edge left and right
     stay equal px from the icons").

     The count hangs off the right of the bag. Measured on the live pill:
     13.7px of air left of the heart, 14.2px right of the bag with no count —
     and 4px right of the count once there is one. So when a count shows, the
     pill gains exactly what it takes for the air right of the count to equal
     the air left of the heart, measured from what is DRAWN (the paths, not the
     boxes: Dawn's bag sits in a 40-unit viewBox with a wide margin built in).
     No count, no room: the pill keeps its original size.

     The room is a margin after the bag, so the pill grows to the right and
     the drift pass below moves it by the same amount — both icons still sit
     exactly above their twins in the header. A two-digit count is wider, and
     is measured like any other. */
  function inkX(svg) {
    if (!svg || !svg.getScreenCTM) return null;
    var m = svg.getScreenCTM();
    if (!m) return null;
    var lo = Infinity;
    var hi = -Infinity;
    var shapes = svg.querySelectorAll('path, circle, rect, line, polyline, polygon, ellipse');
    for (var i = 0; i < shapes.length; i++) {
      var bb;
      try { bb = shapes[i].getBBox(); } catch (e) { continue; }
      if (!bb.width && !bb.height) continue;
      lo = Math.min(lo, m.a * bb.x + m.e);
      hi = Math.max(hi, m.a * (bb.x + bb.width) + m.e);
    }
    return lo < hi ? { left: lo, right: hi } : null;
  }

  function roomForCount() {
    bar.style.setProperty('--ns-float-count-room', '0px');
    var icons = bar.querySelectorAll('.header__icons .header__icon');
    if (icons.length < 2) return;
    var count = icons[icons.length - 1].querySelector('.cart-count-bubble:not([data-ns-fav-count])');
    var c = count && count.getBoundingClientRect();
    if (!c || !c.width) return;
    var heart = inkX(icons[icons.length - 2].querySelector('svg'));
    if (!heart) return;
    var b = bar.getBoundingClientRect();
    var cs = getComputedStyle(bar);
    var innerLeft = b.left + (parseFloat(cs.borderLeftWidth) || 0);
    var innerRight = b.right - (parseFloat(cs.borderRightWidth) || 0);
    var room = Math.round(c.right + (heart.left - innerLeft) - innerRight);
    if (room > 0) bar.style.setProperty('--ns-float-count-room', room + 'px');
  }

  var pending;
  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(align, 80);
  }

  align();
  window.addEventListener('load', align);
  window.addEventListener('resize', schedule, { passive: true });
  /* The header's icons move when the webfont lands and when a count appears. */
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(align);
  document.addEventListener('ns:favorites-changed', schedule);
  document.addEventListener('cart:refresh', schedule);
})();
